import { useState, useEffect } from "react";

const C = {
  bg: 'var(--bg)',
  bgCard: 'var(--bgCard)',
  bgCardHover: 'var(--bgCardHover)',
  border: 'var(--border)',
  borderAccent: 'rgba(6,214,196,0.35)',
  cyan: '#06D6C4',
  cyanDark: '#04B0A0',
  violet: '#7C3AED',
  violetLight: '#8B5CF6',
  amber: '#F59E0B',
  green: '#10B981',
  text: 'var(--text)',
  textMuted: 'var(--textMuted)',
  textFaint: 'var(--textFaint)',
  gradient: 'linear-gradient(135deg, #06D6C4 0%, #7C3AED 100%)',
};

const css = `
  :root {
    --bg: #f8fafc;
    --bgCard: #ffffff;
    --bgCardHover: #f1f5f9;
    --text: #0f172a;
    --textMuted: #64748b;
    --textFaint: #94a3b8;
    --border: rgba(0,0,0,0.1);
    --inputBg: rgba(0,0,0,0.03);
    --navBg: rgba(255,255,255,0.95);
    --sectionBg: rgba(0,0,0,0.02);
  }
  .dark {
    --bg: #030A14;
    --bgCard: #071122;
    --bgCardHover: #0B1A35;
    --text: #EEF2FF;
    --textMuted: #8892A4;
    --textFaint: #4A5568;
    --border: rgba(255,255,255,0.06);
    --inputBg: rgba(255,255,255,0.04);
    --navBg: rgba(3,10,20,0.95);
    --sectionBg: rgba(7,17,34,0.5);
  }

  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800&family=Figtree:wght@300;400;500;600;700&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: var(--bg); font-family: 'Figtree', system-ui, sans-serif; color: var(--text); transition: background-color 0.3s, color 0.3s; }
  h1,h2,h3,h4,h5 { font-family: 'Bricolage Grotesque', system-ui, sans-serif; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--textFaint); border-radius: 3px; }
  a { text-decoration: none; color: inherit; }
  .btn-primary { background: linear-gradient(135deg, #06D6C4, #7C3AED); color: #fff; border: none; border-radius: 10px; padding: 12px 24px; font-family: 'Bricolage Grotesque', sans-serif; font-weight: 700; font-size: 15px; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; gap: 8px; letter-spacing: -0.01em; }
  .btn-primary:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 8px 24px rgba(6,214,196,0.25); }
  .btn-outline { background: transparent; color: var(--text); border: 1.5px solid var(--border); border-radius: 10px; padding: 12px 24px; font-family: 'Bricolage Grotesque', sans-serif; font-weight: 700; font-size: 15px; cursor: pointer; transition: all 0.2s; display: inline-flex; align-items: center; gap: 8px; letter-spacing: -0.01em; }
  .btn-outline:hover { border-color: #06D6C4; color: #06D6C4; background: rgba(6,214,196,0.06); }
  .card { background: var(--bgCard); border: 1px solid var(--border); border-radius: 16px; transition: all 0.25s; }
  .card:hover { border-color: rgba(6,214,196,0.35); background: var(--bgCardHover); transform: translateY(-2px); }
  .badge { display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px; border-radius: 999px; font-size: 12px; font-weight: 700; font-family: 'Bricolage Grotesque', sans-serif; letter-spacing: 0.04em; text-transform: uppercase; }
  .badge-cyan { background: rgba(6,214,196,0.1); color: #06D6C4; border: 1px solid rgba(6,214,196,0.2); }
  .badge-violet { background: rgba(124,58,237,0.1); color: #8B5CF6; border: 1px solid rgba(124,58,237,0.2); }
  .badge-amber { background: rgba(245,158,11,0.1); color: #F59E0B; border: 1px solid rgba(245,158,11,0.2); }
  .badge-green { background: rgba(16,185,129,0.1); color: #10B981; border: 1px solid rgba(16,185,129,0.2); }
  .section-title { font-size: clamp(32px, 5vw, 52px); font-weight: 800; line-height: 1.1; letter-spacing: -0.03em; }
  .gradient-text { background: linear-gradient(135deg, #06D6C4 0%, #A78BFA 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .nav-link { color: var(--textMuted); font-size: 14px; font-weight: 600; cursor: pointer; transition: color 0.2s; padding: 4px 0; font-family: 'Figtree', sans-serif; letter-spacing: -0.01em; }
  .nav-link:hover { color: var(--text); }
  .faq-item { border-bottom: 1px solid var(--border); }
  input, textarea, select { background: var(--inputBg); border: 1px solid var(--border); border-radius: 10px; padding: 12px 16px; color: var(--text); font-family: 'Figtree', sans-serif; font-size: 14px; width: 100%; outline: none; transition: border-color 0.2s; }
  input:focus, textarea:focus, select:focus { border-color: #06D6C4; background: rgba(6,214,196,0.04); }
  input::placeholder, textarea::placeholder { color: var(--textFaint); }
  select option { background: var(--bgCard); color: var(--text); }
`;

function NavHeader({ page, setPage, isDark, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMobileOpen(false); };

  const navStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    background: scrolled ? 'var(--navBg)' : 'transparent',
    backdropFilter: scrolled ? 'blur(20px)' : 'none',
    borderBottom: scrolled ? `1px solid ${C.border}` : 'none',
    transition: 'all 0.3s',
    padding: '0 clamp(16px, 4vw, 60px)',
  };

  return (
    <nav style={navStyle}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 68 }}>
        <div onClick={() => setPage('home')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: C.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 16 }}>C</span>
          </div>
          <div>
            <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 15, color: C.text, lineHeight: 1, letterSpacing: '-0.02em' }}>CORE</div>
            <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.2, fontFamily: 'Figtree' }}>chateacontuempresa.com</div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }} className="desktop-nav">
          {[['características','caracteristicas'],['precios','precios'],['demo','demo'],['FAQ','faq'],['contacto','contacto']].map(([l, id]) => (
            <span key={id} className="nav-link" onClick={() => scrollTo(id)}>{l}</span>
          ))}
          <span className="nav-link" onClick={() => setPage('docs')}>docs</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={toggleTheme} style={{ background: 'var(--inputBg)', border: `1px solid ${C.border}`, borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s' }}>
            {isDark ? (
              <svg style={{width: 18, height: 18, color: '#eab308'}} fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"></path></svg>
            ) : (
              <svg style={{width: 18, height: 18, color: '#94a3b8'}} fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path></svg>
            )}
          </button>
          <a href="/login.php">
            <button className="btn-outline" style={{ padding: '9px 18px', fontSize: 13 }}>Iniciar sesión</button>
          </a>
          <button className="btn-primary" style={{ padding: '9px 18px', fontSize: 13 }} onClick={() => scrollTo('descargar')}>
            ⬇ Descargar
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} style={{ display: 'none', background: 'transparent', border: 'none', color: C.text, cursor: 'pointer', fontSize: 24 }} id="hamburger">☰</button>
        </div>
      </div>

      {mobileOpen && (
        <div style={{ background: 'var(--navBg)', padding: '20px 24px', borderTop: `1px solid ${C.border}` }}>
          {[['Características','caracteristicas'],['Precios','precios'],['Demo','demo'],['FAQ','faq'],['Contacto','contacto']].map(([l, id]) => (
            <div key={id} onClick={() => scrollTo(id)} style={{ padding: '14px 0', borderBottom: `1px solid ${C.border}`, color: C.text, cursor: 'pointer', fontFamily: 'Bricolage Grotesque', fontWeight: 600 }}>{l}</div>
          ))}
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <a href="/login.php"><button className="btn-outline" style={{ width: '100%', justifyContent: 'center' }}>Iniciar sesión</button></a>
            <button className="btn-primary" style={{ justifyContent: 'center' }} onClick={() => scrollTo('descargar')}>⬇ Descargar gratis</button>
          </div>
        </div>
      )}
    </nav>
  );
}

function DownloadCards() {
  const [macOpen, setMacOpen] = useState(false);

  const platforms = [
    {
      id: 'mac',
      icon: (
        <svg viewBox="0 0 814 1000" fill="currentColor" style={{ width: 44, height: 44 }}>
          <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-167.3-127.8c-57.1-75.2-100-191.7-100-304.1 0-197.6 129.7-302 256.7-302 67.3 0 123.3 44.4 165.7 44.4 40.6 0 103.8-46.8 173.1-46.8zm-96.7-88.8c32.1-38.8 55.5-93.2 55.5-147.6 0-7.7-.6-15.4-1.9-21.7-52.3 2-114.5 35-151.8 77.5-28.8 32.8-56 88.8-56 143.8 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.4 1.3 13.5 1.3 46.8 0 105.2-31.5 138.8-72.5z"/>
        </svg>
      ),
      label: 'Mac',
      sub: 'macOS 12.0+',
      accent: '#94a3b8',
      primary: { label: 'Apple Silicon (M1/M2/M3)', ext: '.dmg', url: 'https://github.com/chateacontuempresa/core-chateacontuempresa/releases/download/v1.0.18/Chatea.con.tu.Empresa-1.0.18-arm64.dmg' },
      secondary: { label: 'Intel', ext: '.dmg', url: 'https://github.com/chateacontuempresa/core-chateacontuempresa/releases/download/v1.0.18/Chatea.con.tu.Empresa-1.0.18.dmg' },
    },
    {
      id: 'windows',
      icon: (
        <svg viewBox="0 0 88 88" fill="currentColor" style={{ width: 44, height: 44 }}>
          <path d="M0 12.402l35.687-4.86.016 34.423-35.67.203zm35.67 33.529l.028 34.453L.028 75.48.026 45.7zm4.326-39.025L87.314 0v41.527l-47.318.376zm47.329 39.349l-.011 41.314-47.318-6.678-.066-34.78z"/>
        </svg>
      ),
      label: 'Windows',
      sub: 'Windows 10, 11',
      accent: '#4FC3F7',
      primary: { label: 'Download for Windows (x64)', ext: '.exe', url: 'https://github.com/chateacontuempresa/core-chateacontuempresa/releases/download/v1.0.18/Chatea.con.tu.Empresa.Setup.1.0.18.exe' },
      secondary: null,
    },
    {
      id: 'linux',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 44, height: 44 }}>
          <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.385-.187-.863-.43-1.374a2.208 2.208 0 00.171-.434c.146-.65-.05-1.42-.391-2.163-.34-.744-.886-1.465-1.38-1.896-.492-.43-1.061-.523-1.538-.31a2.15 2.15 0 00-.392.23c-.274-.408-.582-.76-.94-1.049-.427-.345-.88-.576-1.302-.71-.422-.136-.806-.162-1.132-.111a1.93 1.93 0 00-.743.276c-.335-.49-.673-.977-.999-1.313-.325-.337-.618-.51-.896-.52z"/>
        </svg>
      ),
      label: 'Linux',
      sub: '.AppImage',
      accent: '#FFA726',
      primary: { label: 'Download .AppImage (x64)', ext: '.AppImage', url: 'https://github.com/chateacontuempresa/core-chateacontuempresa/releases/download/v1.0.18/Chatea.con.tu.Empresa-1.0.18.AppImage' },
      secondary: null,
    },
  ];

  return (
    <div id="descargar" style={{ marginBottom: 32 }}>
      <p style={{ fontSize: 12, color: C.textMuted, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'Bricolage Grotesque', fontWeight: 700 }}>
        Descargar aplicación
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {platforms.map((p) => (
          <div
            key={p.id}
            style={{
              background: 'var(--bgCard)',
              border: `1px solid ${C.border}`,
              borderRadius: 16,
              padding: '24px 14px 16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.25s',
              position: 'relative',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = p.accent + '80'; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${p.accent}20`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{ color: C.text, opacity: 0.8, marginBottom: 4 }}>{p.icon}</div>
            <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 17, color: C.text, letterSpacing: '-0.02em' }}>{p.label}</div>
            <div style={{ fontSize: 12, color: C.textMuted, fontFamily: 'Figtree', marginBottom: 10 }}>{p.sub}</div>

            {p.secondary ? (
              <div style={{ width: '100%', position: 'relative' }}>
                <div style={{ display: 'flex', width: '100%', borderRadius: 9, overflow: 'hidden' }}>
                  <a
                    href={p.primary.url}
                    download
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, background: p.accent, padding: '9px 8px', fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 11, color: '#fff', letterSpacing: '-0.01em', textDecoration: 'none', transition: 'opacity 0.15s', lineHeight: 1.3, textAlign: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    ⬇ Apple Silicon
                  </a>
                  <button
                    onClick={() => setMacOpen(!macOpen)}
                    style={{ background: p.accent, border: 'none', borderLeft: '1px solid rgba(255,255,255,0.25)', padding: '9px 10px', cursor: 'pointer', color: '#fff', fontSize: 11, transition: 'opacity 0.15s', display: 'flex', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    {macOpen ? '▲' : '▼'}
                  </button>
                </div>
                {macOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, background: 'var(--bgCard)', border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
                    <a
                      href={p.secondary.url}
                      download
                      onClick={() => setMacOpen(false)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', fontSize: 13, color: C.text, fontFamily: 'Figtree', fontWeight: 500, textDecoration: 'none', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bgCardHover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      ⬇ macOS Intel <span style={{ color: C.textFaint, fontSize: 11, marginLeft: 4 }}>.dmg</span>
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <a
                href={p.primary.url}
                download
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: p.accent, borderRadius: 9, padding: '9px 10px', fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 11, color: '#fff', letterSpacing: '-0.01em', textDecoration: 'none', transition: 'opacity 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                ⬇ {p.primary.label}
              </a>
            )}
          </div>
        ))}
      </div>
      <p style={{ fontSize: 11, color: C.textFaint, marginTop: 10, fontFamily: 'Figtree' }}>
        v1.0.18 · Windows 10/11 · macOS 12+ (Apple Silicon &amp; Intel) · Ubuntu 20.04+
      </p>
    </div>
  );
}

function HeroSection() {
  return (
    <section id="inicio" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', paddingTop: 100, padding: 'clamp(80px,10vw,140px) clamp(16px,4vw,60px) 80px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, background: 'radial-gradient(circle, rgba(6,214,196,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '30%', right: '-5%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 60, alignItems: 'center' }}>

          <div>
            <div style={{ marginBottom: 20 }}>
              <span className="badge badge-cyan">✦ IA On-Premise Empresarial</span>
              <span className="badge badge-green" style={{ marginLeft: 8 }}>🔒 100% Privado</span>
            </div>

            <h1 className="section-title" style={{ marginBottom: 20 }}>
              Chatea con tus
              <span className="gradient-text"> datos empresariales</span>
              <br />en segundos
            </h1>

            <p style={{ fontSize: 17, color: C.textMuted, lineHeight: 1.7, marginBottom: 32, maxWidth: 500, fontFamily: 'Figtree' }}>
              Conecta tus bases de datos y permite que tu equipo consulte información en lenguaje natural via WhatsApp, email o dashboard.{' '}
              <strong style={{ color: C.text, fontFamily: 'Figtree', fontWeight: 600 }}>Instalación en tu servidor. Tus datos nunca salen de tu empresa.</strong>
            </p>

            {/* ── DOWNLOAD SECTION ── */}
            <DownloadCards />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button className="btn-primary" style={{ fontSize: 16, padding: '14px 28px' }} onClick={() => document.getElementById('precios')?.scrollIntoView({ behavior: 'smooth' })}>
                Obtener licencia — desde $20/mes
              </button>
              <button className="btn-outline" onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}>
                ▶ Ver demo en vivo
              </button>
            </div>
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ background: 'linear-gradient(135deg, rgba(6,214,196,0.08), rgba(124,58,237,0.08))', border: `1px solid ${C.borderAccent}`, borderRadius: 20, padding: 24, backdropFilter: 'blur(10px)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                {['#FF5F57','#FEBC2E','#28C840'].map(c => <div key={c} style={{ width: 12, height: 12, borderRadius: '50%', background: c }} />)}
                <div style={{ flex: 1, background: 'var(--inputBg)', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: C.textFaint, fontFamily: 'Figtree' }}>core.chateacontuempresa.com</div>
              </div>
              {[
                { role: 'user', msg: '¿Cuáles fueron las ventas de esta semana por producto?' },
                { role: 'ai', msg: '📊 Aquí tienes las ventas de la semana:\n\nProducto A: $24,300 (+12%)\nProducto B: $18,750 (+5%)\nProducto C: $9,200 (-3%)\n\nTotal: $52,250 ↑ 7% vs semana anterior' },
                { role: 'user', msg: '¿Qué vendedor tuvo mejor desempeño?' },
                { role: 'ai', msg: '🏆 Carlos Méndez lideró con $18,400 en ventas (35% del total). ¿Quieres ver el desglose por cliente?' },
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                  <div style={{ maxWidth: '80%', background: m.role === 'user' ? 'linear-gradient(135deg, #06D6C4, #7C3AED)' : 'var(--inputBg)', borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', padding: '10px 14px', fontSize: 13, color: C.text, lineHeight: 1.5, whiteSpace: 'pre-line', fontFamily: 'Figtree' }}>
                    {m.role === 'ai' && <div style={{ fontSize: 10, color: C.cyan, fontFamily: 'Bricolage Grotesque', fontWeight: 700, marginBottom: 4, letterSpacing: '0.05em' }}>CORE AI</div>}
                    {m.msg}
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <input placeholder="Escribe una consulta en lenguaje natural..." style={{ flex: 1, fontSize: 13, padding: '10px 14px' }} />
                <button className="btn-primary" style={{ padding: '10px 16px', borderRadius: 10, fontSize: 18 }}>↑</button>
              </div>
            </div>

            <div style={{ position: 'absolute', top: -16, right: -16, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#25D366', fontSize: 16 }}>📱</span>
              <span style={{ fontSize: 12, fontFamily: 'Bricolage Grotesque', fontWeight: 700 }}>WhatsApp Ready</span>
            </div>
            <div style={{ position: 'absolute', bottom: -16, left: -16, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: C.cyan, fontSize: 14 }}>🔒</span>
              <span style={{ fontSize: 12, fontFamily: 'Bricolage Grotesque', fontWeight: 700 }}>On-Premise</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1, marginTop: 72, borderTop: `1px solid ${C.border}`, paddingTop: 48 }}>
          {[['100+','Empresas instaladas'],['10M+','Consultas procesadas'],['50+','Conectores BD'],['24/7','Agentes IA activos'],['1-3 días','Implementación promedio']].map(([n, l]) => (
            <div key={n} style={{ textAlign: 'center', padding: '0 16px' }}>
              <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 36, background: C.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.03em' }}>{n}</div>
              <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4, fontFamily: 'Figtree' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    { icon: '🗄️', title: 'Multi-Base de Datos', desc: 'MySQL, SQL Server, PostgreSQL, Oracle, MongoDB. La IA entiende tu esquema y genera SQL automáticamente.', color: C.cyan },
    { icon: '💬', title: 'WhatsApp Business', desc: 'Consultas por WhatsApp, email y dashboard. Respuestas con tablas y gráficos al instante.', color: '#25D366' },
    { icon: '🤖', title: 'Agentes IA 24/7', desc: 'Configura agentes que monitoran tus datos y envían alertas automáticas cuando se cumplen condiciones.', color: C.violetLight },
    { icon: '📊', title: 'Dashboards Auto', desc: 'Describe lo que quieres ver y CORE crea el dashboard con gráficos BI automáticamente.', color: C.amber },
    { icon: '📄', title: 'Reportes PDF', desc: 'Reportes ejecutivos en PDF enviados por email de forma programada. Semanal, mensual o personalizado.', color: '#F87171' },
    { icon: '🔒', title: '100% On-Premise', desc: 'Instala en tu servidor. Tus datos nunca salen de tu infraestructura. Ideal para bancos y salud.', color: C.green },
    { icon: '👥', title: 'Multi-Departamento', desc: 'Agentes especializados por área: Ventas, RH, Inventario. Roles y permisos granulares.', color: '#60A5FA' },
    { icon: '🔗', title: 'APIs & Webhooks', desc: 'Integra con tus sistemas existentes vía REST API. Compatible con Zapier, Make y más.', color: '#C084FC' },
  ];

  return (
    <section id="caracteristicas" style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,60px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <span className="badge badge-violet" style={{ marginBottom: 16 }}>✦ Características</span>
          <h2 className="section-title" style={{ marginBottom: 16 }}>
            Todo lo que tu empresa <span className="gradient-text">necesita</span>
          </h2>
          <p style={{ fontSize: 18, color: C.textMuted, maxWidth: 520, margin: '0 auto', fontFamily: 'Figtree' }}>
            Transforma tus datos en conversaciones inteligentes. Sin mover nada de tu servidor.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {features.map(({ icon, title, desc, color }) => (
            <div key={title} className="card" style={{ padding: 24 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 16 }}>
                {icon}
              </div>
              <h3 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 17, marginBottom: 8, color: C.text, letterSpacing: '-0.02em' }}>{title}</h3>
              <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.6, fontFamily: 'Figtree' }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoSection() {
  const [active, setActive] = useState(0);

  const videos = [
    { title: '¡Instala tu propia IA Empresarial! 🚀', desc: 'Chatea con tus Bases de Datos y Documentos (100% Privado)', ytId: 'BJvIypEFlyE', duration: '7:17' },
    { title: 'Chatea Con tu informacion', desc: 'Haz preguntas a tu información empresarial', ytId: 'BJvIypEFlyE', duration: '00:40' },
    { title: 'Conexión a Base de Datos', desc: 'Conecta tu BD en minutos', ytId: 'BJvIypEFlyE', duration: '1:30' },
    { title: 'Dashboard Automático', desc: 'Crea reportes visuales al instante', ytId: 'BJvIypEFlyE', duration: '1:45' },
  ];

  return (
    <section id="demo" style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,60px)', background: 'var(--sectionBg)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <span className="badge badge-cyan" style={{ marginBottom: 16 }}>▶ Demostración</span>
          <h2 className="section-title" style={{ marginBottom: 16 }}>Mira CORE <span className="gradient-text">en acción</span></h2>
          <p style={{ fontSize: 18, color: C.textMuted, fontFamily: 'Figtree' }}>Descubre cómo tu equipo puede consultar datos empresariales en segundos</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <div style={{ borderRadius: 20, overflow: 'hidden', background: '#000', aspectRatio: '16/9', position: 'relative' }}>
              <iframe
                width="100%" height="100%"
                src={`https://www.youtube.com/embed/${videos[active].ytId}?rel=0&modestbranding=1`}
                title={videos[active].title}
                style={{ border: 'none', position: 'absolute', inset: 0 }}
                allowFullScreen
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <h3 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 20, letterSpacing: '-0.02em' }}>{videos[active].title}</h3>
              <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4, fontFamily: 'Figtree' }}>{videos[active].desc}</p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {videos.map((v, i) => (
              <div key={i} onClick={() => setActive(i)} style={{ background: active === i ? 'rgba(6,214,196,0.08)' : C.bgCard, border: `1.5px solid ${active === i ? C.borderAccent : C.border}`, borderRadius: 12, padding: 16, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: active === i ? 'linear-gradient(135deg, #06D6C4, #7C3AED)' : 'var(--inputBg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18, color: active === i ? '#fff' : C.text }}>▶</div>
                <div>
                  <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em' }}>{v.title}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, fontFamily: 'Figtree' }}>{v.desc} · {v.duration}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const [period, setPeriod] = useState('year');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);

  const periods = [
    { key: '1month', label: '1 mes', discount: 0 },
    { key: '3months', label: '3 meses', discount: 15 },
    { key: '6months', label: '6 meses', discount: 25 },
    { key: 'year', label: '1 año', discount: 40 },
  ];

  const plans = [
    {
      name: 'Starter', base: 20, color: C.cyan, badge: null,
      features: ['1 base de datos','Hasta 10 usuarios','Consultas por WhatsApp','Dashboards básicos','Soporte por email','Actualizaciones incluidas'],
    },
    {
      name: 'Professional', base: 49, color: C.violetLight, badge: 'MÁS POPULAR',
      features: ['Hasta 5 bases de datos','Usuarios ilimitados','WhatsApp + Email + Dashboard','Agentes IA programables','Reportes PDF automáticos','Soporte prioritario 24/7','Capacitación inicial (8h)'],
    },
    {
      name: 'Enterprise', base: null, color: C.amber, badge: null,
      features: ['Bases de datos ilimitadas','Usuarios ilimitados','Todos los canales','Agentes IA ilimitados','Multi-departamento','SLA garantizado','Gerente de cuenta dedicado','Código personalizado'],
    },
  ];

  const getPrice = (base) => {
    if (!base) return null;
    const d = periods.find(p => p.key === period)?.discount || 0;
    return (base * (1 - d / 100)).toFixed(0);
  };

  const getTotal = (base) => {
    if (!base) return null;
    const months = period === '1month' ? 1 : period === '3months' ? 3 : period === '6months' ? 6 : 12;
    return (getPrice(base) * months).toFixed(0);
  };

  const handleBuy = (plan) => {
    setSelectedPlan(plan);
    setShowPayPal(true);
  };

  return (
    <section id="precios" style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,60px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <span className="badge badge-green" style={{ marginBottom: 16 }}>💳 Licencias</span>
          <h2 className="section-title" style={{ marginBottom: 16 }}>
            Precios <span className="gradient-text">transparentes</span>
          </h2>
          <p style={{ fontSize: 18, color: C.textMuted, fontFamily: 'Figtree' }}>Sin costos ocultos. Tu servidor, tus datos, tu licencia.</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48 }}>
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, display: 'flex', gap: 4 }}>
            {periods.map(({ key, label, discount }) => (
              <button key={key} onClick={() => setPeriod(key)} style={{ padding: '8px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 13, background: period === key ? 'linear-gradient(135deg, #06D6C4, #7C3AED)' : 'transparent', color: period === key ? '#fff' : C.textMuted, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '-0.01em' }}>
                {label}
                {discount > 0 && <span style={{ background: period === key ? 'rgba(255,255,255,0.2)' : 'rgba(16,185,129,0.15)', color: period === key ? '#fff' : C.green, fontSize: 11, padding: '2px 7px', borderRadius: 99 }}>-{discount}%</span>}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {plans.map((plan) => {
            const price = getPrice(plan.base);
            const total = getTotal(plan.base);
            const months = period === '1month' ? 1 : period === '3months' ? 3 : period === '6months' ? 6 : 12;
            const popular = plan.badge === 'MÁS POPULAR';

            return (
              <div key={plan.name} style={{ background: popular ? `linear-gradient(180deg, rgba(124,58,237,0.12) 0%, ${C.bgCard} 100%)` : C.bgCard, border: `${popular ? 2 : 1}px solid ${popular ? C.violetLight : C.border}`, borderRadius: 20, padding: 32, position: 'relative', transition: 'all 0.25s' }}>
                {popular && (
                  <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #7C3AED, #6366F1)', color: '#fff', fontSize: 11, fontFamily: 'Bricolage Grotesque', fontWeight: 700, padding: '4px 16px', borderRadius: 99, letterSpacing: '0.06em' }}>✦ MÁS POPULAR</div>
                )}

                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 22, color: plan.color, letterSpacing: '-0.02em' }}>{plan.name}</span>
                </div>

                <div style={{ marginBottom: 24 }}>
                  {plan.base ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 900, fontSize: 48, color: C.text, letterSpacing: '-0.04em' }}>${price}</span>
                        <span style={{ color: C.textMuted, fontSize: 16, fontFamily: 'Figtree' }}>/mes</span>
                      </div>
                      {months > 1 && (
                        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4, fontFamily: 'Figtree' }}>
                          Total: <strong style={{ color: C.text }}>${total}</strong> por {months} meses
                          {periods.find(p => p.key === period)?.discount > 0 && (
                            <span style={{ marginLeft: 8, color: C.green }}>· Ahorras {periods.find(p => p.key === period)?.discount}%</span>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 36, color: C.text, letterSpacing: '-0.03em' }}>A medida</div>
                  )}
                </div>

                <ul style={{ listStyle: 'none', marginBottom: 28, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {plan.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: C.textMuted, fontFamily: 'Figtree' }}>
                      <span style={{ color: plan.color, flexShrink: 0, marginTop: 1 }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <button onClick={() => plan.base ? handleBuy(plan) : document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
                  style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 15, background: popular ? 'linear-gradient(135deg, #7C3AED, #6366F1)' : `${plan.color}18`, color: popular ? '#fff' : plan.color, transition: 'all 0.2s', borderTop: `1.5px solid ${popular ? 'transparent' : `${plan.color}40`}`, letterSpacing: '-0.01em' }}
                  onMouseEnter={e => { if (!popular) { e.currentTarget.style.background = `${plan.color}28`; } }}
                  onMouseLeave={e => { if (!popular) { e.currentTarget.style.background = `${plan.color}18`; } }}>
                  {plan.base ? `Comprar ${plan.name}` : 'Contactar Ventas'}
                </button>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 40, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, textAlign: 'center' }}>
          <h3 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 20, marginBottom: 8, letterSpacing: '-0.02em' }}>¿Prefieres licencia perpetua?</h3>
          <p style={{ color: C.textMuted, fontSize: 14, marginBottom: 20, fontFamily: 'Figtree' }}>Adquiere una licencia de por vida con un solo pago. Incluye 1 año de actualizaciones y soporte.</p>
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 16 }}>
            {[['Starter','$699',C.cyan],['Professional','$1,499',C.violetLight],['Enterprise','A medida',C.amber]].map(([n, p, c]) => (
              <div key={n} style={{ background: `${c}0D`, border: `1px solid ${c}30`, borderRadius: 12, padding: '16px 24px', textAlign: 'center' }}>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 24, color: c, letterSpacing: '-0.03em' }}>{p}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, fontFamily: 'Figtree' }}>{n} · Pago único</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showPayPal && selectedPlan && (
        <PayPalModal plan={selectedPlan} period={period} periods={periods} getPrice={getPrice} getTotal={getTotal} onClose={() => setShowPayPal(false)} />
      )}
    </section>
  );
}

function PayPalModal({ plan, period, periods, getPrice, getTotal, onClose }) {
  const total = getTotal(plan.base);
  const pLabel = periods.find(p => p.key === period)?.label;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: 'var(--bgCard)', border: `1px solid ${C.borderAccent}`, borderRadius: 20, padding: 36, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 22, color: C.text, letterSpacing: '-0.02em' }}>Iniciar sesión requerida</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>✕</button>
        </div>

        <p style={{ color: C.textMuted, fontSize: 15, marginBottom: 24, lineHeight: 1.6, fontFamily: 'Figtree' }}>
          Para adquirir el plan <strong style={{ color: plan.color }}>{plan.name}</strong>, necesitas acceder a tu panel de cliente.
        </p>

        <div style={{ background: 'rgba(6,214,196,0.06)', border: `1px solid ${C.borderAccent}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: C.textMuted, fontSize: 14, fontFamily: 'Figtree' }}>Plan seleccionado</span>
            <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, color: plan.color }}>{plan.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: C.textMuted, fontSize: 14, fontFamily: 'Figtree' }}>Período</span>
            <span style={{ fontSize: 14, color: C.text, fontFamily: 'Figtree' }}>{pLabel}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 4 }}>
            <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, color: C.text }}>Total a pagar</span>
            <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 22, color: C.cyan, letterSpacing: '-0.03em' }}>${total} USD</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button className="btn-primary" style={{ width: '100%', justifyContent: 'center', fontSize: 16, padding: 16 }} onClick={() => window.location.href = '/login.php'}>
            Ir a Iniciar Sesión / Registro →
          </button>
          <button style={{ width: '100%', background: 'transparent', border: `1px solid ${C.border}`, color: C.textMuted, borderRadius: 12, padding: 16, cursor: 'pointer', fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 15 }} onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function TestimonialsSection() {
  const tests = [
    { name: 'Roberto Méndez', role: 'CEO, Grupo Comercial del Norte', text: 'Instalamos el sistema en nuestro servidor y en una semana todo el equipo consultaba ventas por WhatsApp. La seguridad de tener los datos en nuestra infraestructura fue clave.', init: 'RM', color: C.cyan },
    { name: 'Ana Patricia Gómez', role: 'Directora de Ops, LogiTrans', text: 'Los agentes IA que monitorean inventario 24/7 nos han ahorrado miles en stock muerto. Los reportes PDF son perfectos para la junta directiva.', init: 'AG', color: C.violetLight },
    { name: 'Carlos Herrera', role: 'Gerente TI, Banco Regional', text: 'Como banco no podíamos usar un SaaS externo. La versión on-premise nos da control total. La instalación fue sencilla y el soporte técnico es excepcional.', init: 'CH', color: C.amber },
    { name: 'María Fernanda López', role: 'Directora RH, Corp. San José', text: 'El módulo de RH consulta nómina, asistencia y vacaciones. Mis empleados ya no me buscan para reportes. La IA responde todo por WhatsApp.', init: 'ML', color: C.green },
    { name: 'Jorge Ramírez', role: 'CFO, Industrias Unidas', text: 'Los dashboards auto-generados son increíbles. Digo "quiero ver flujo de caja por mes" y crea el gráfico perfecto. 20 horas semanales ahorradas en reportes.', init: 'JR', color: '#F87171' },
    { name: 'Laura Sánchez', role: 'Gerente Ventas, Dist. Continental', text: 'Mi equipo consulta precios, inventario e historial de clientes desde el celular. Cerramos más ventas porque tienen información instantánea en la llamada.', init: 'LS', color: '#60A5FA' },
  ];

  return (
    <section id="testimonios" style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,60px)', background: 'var(--sectionBg)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <span className="badge badge-amber" style={{ marginBottom: 16 }}>★ Testimonios</span>
          <h2 className="section-title" style={{ marginBottom: 16 }}>Empresas que <span className="gradient-text">confían en CORE</span></h2>
          <p style={{ fontSize: 18, color: C.textMuted, fontFamily: 'Figtree' }}>Más de 100 empresas han transformado su acceso a datos</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {tests.map(({ name, role, text, init, color }) => (
            <div key={name} className="card" style={{ padding: 28 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {[...Array(5)].map((_, i) => <span key={i} style={{ color: C.amber, fontSize: 14 }}>★</span>)}
              </div>
              <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.7, marginBottom: 20, fontStyle: 'italic', fontFamily: 'Figtree' }}>"{text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${color}20`, border: `1.5px solid ${color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 14, color: color, flexShrink: 0 }}>{init}</div>
                <div>
                  <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em' }}>{name}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, fontFamily: 'Figtree' }}>{role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState(0);
  const faqs = [
    ['¿Qué significa que sea on-premise?', 'On-premise significa que el sistema se instala en tu propio servidor dentro de tu infraestructura. Tus datos nunca salen de tu empresa, a diferencia de un SaaS donde los datos se almacenan en servidores externos. Esto te da control total y cumple con regulaciones de seguridad estrictas.'],
    ['¿Qué bases de datos son compatibles?', 'Soportamos MySQL, Microsoft SQL Server, PostgreSQL, Oracle, MariaDB y MongoDB. También podemos conectar APIs REST, archivos Excel/CSV y sistemas legacy mediante conectores personalizados.'],
    ['¿Cómo funciona la integración con WhatsApp?', 'Utilizamos la API oficial de WhatsApp Business (Meta). Tus empleados envían mensajes a un número dedicado y la IA responde con datos de tu empresa. También soportamos Twilio y otros proveedores de WhatsApp API.'],
    ['¿Qué tan difícil es la instalación?', 'La instalación típica toma 2-4 horas. Proveemos instaladores para Windows Server y Linux (Ubuntu, CentOS, RHEL). Nuestro equipo puede hacer la instalación remota o capacitar a tu equipo de TI.'],
    ['¿Necesito conocimientos de programación?', 'No. El sistema está diseñado para usuarios no técnicos. Conectar una base de datos es un proceso guiado paso a paso. Los empleados hacen preguntas en lenguaje natural, sin SQL ni fórmulas.'],
    ['¿Puedo personalizar la IA para mi industria?', 'Sí. El sistema aprende de tus datos y se adapta a tu vocabulario empresarial. En el plan Enterprise podemos entrenar modelos específicos para tu industria.'],
    ['¿Cómo se maneja la seguridad y los permisos?', 'Cada usuario tiene roles y permisos configurables. Puedes restringir acceso por departamento, tipo de dato o nivel de sensibilidad. El sistema registra un audit log de todas las consultas.'],
    ['¿Puedo aplicar pagos anteriores a una licencia perpetua?', 'Sí. Puedes aplicar el 50% de lo pagado en suscripción mensual/anual hacia la compra de una licencia perpetua. La licencia perpetua incluye actualizaciones por 1 año.'],
  ];

  return (
    <section id="faq" style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,60px)' }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <span className="badge badge-violet" style={{ marginBottom: 16 }}>? Preguntas frecuentes</span>
          <h2 className="section-title">Resolvemos <span className="gradient-text">tus dudas</span></h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {faqs.map(([q, a], i) => (
            <div key={i} style={{ background: open === i ? C.bgCardHover : C.bgCard, border: `1px solid ${open === i ? C.borderAccent : C.border}`, borderRadius: 12, overflow: 'hidden', transition: 'all 0.2s' }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 16 }}>
                <span style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 16, color: C.text, letterSpacing: '-0.01em' }}>{q}</span>
                <span style={{ color: C.cyan, fontSize: 20, flexShrink: 0, transition: 'transform 0.2s', transform: open === i ? 'rotate(45deg)' : 'none' }}>+</span>
              </button>
              {open === i && (
                <div style={{ padding: '0 24px 20px', color: C.textMuted, fontSize: 15, lineHeight: 1.7, fontFamily: 'Figtree' }}>{a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:chateacontuempresa@gmail.com?subject=Consulta de ${form.company}&body=Nombre: ${form.name}%0AEmail: ${form.email}%0AEmpresa: ${form.company}%0A%0A${form.message}`;
    window.location.href = mailtoLink;
    setSent(true);
  };

  return (
    <section id="contacto" style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,60px)', background: 'var(--sectionBg)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <span className="badge badge-cyan" style={{ marginBottom: 16 }}>✉ Contacto</span>
          <h2 className="section-title">¿Listo para <span className="gradient-text">implementar?</span></h2>
          <p style={{ fontSize: 18, color: C.textMuted, marginTop: 12, fontFamily: 'Figtree' }}>Nuestro equipo de ingeniería te acompaña en todo el proceso</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48 }}>
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 32 }}>
              {[
                { icon: '📧', title: 'Email de Ventas', sub: 'chateacontuempresa@gmail.com', note: 'Respuesta en menos de 4 horas' },
                { icon: '🛠', title: 'Soporte Técnico', sub: 'chateacontuempresa@gmail.com', note: '24/7 para clientes Enterprise' },
                { icon: '📅', title: 'Agendar Demo', sub: 'Demo personalizada de 30 min', note: 'Lun-Vie 9AM-6PM CST' },
                { icon: '📍', title: 'Oficinas', sub: 'Ciudad de México, México', note: 'Atención con cita previa' },
              ].map(({ icon, title, sub, note }) => (
                <div key={title} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(6,214,196,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
                  <div>
                    <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 15, color: C.text, letterSpacing: '-0.01em' }}>{title}</div>
                    <div style={{ fontSize: 13, color: C.textMuted, marginTop: 2, fontFamily: 'Figtree' }}>{sub}</div>
                    <div style={{ fontSize: 12, color: C.textFaint, marginTop: 2, fontFamily: 'Figtree' }}>{note}</div>
                  </div>
                </div>
              ))}
            </div>

            <a href="https://wa.me/50249899115?text=Hola,%20quiero%20información%20sobre%20CORE%20-%20chateacontuempresa.com" target="_blank" rel="noreferrer">
              <button style={{ width: '100%', background: '#25D366', border: 'none', borderRadius: 12, padding: '16px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 16, color: '#fff', transition: 'all 0.2s', letterSpacing: '-0.01em' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <span style={{ fontSize: 22 }}>📱</span>
                Contactar por WhatsApp
              </button>
            </a>
            <p style={{ fontSize: 12, color: C.textFaint, textAlign: 'center', marginTop: 8, fontFamily: 'Figtree' }}>+502 4989 99115 · Respuesta inmediata</p>
          </div>

          <div>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
                <h3 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 24, marginBottom: 8, letterSpacing: '-0.02em' }}>¡Mensaje enviado!</h3>
                <p style={{ color: C.textMuted, fontFamily: 'Figtree' }}>Un especialista te contactará en menos de 24 horas.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, color: C.textMuted, display: 'block', marginBottom: 6, fontFamily: 'Figtree' }}>Nombre completo</label>
                    <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Tu nombre" />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: C.textMuted, display: 'block', marginBottom: 6, fontFamily: 'Figtree' }}>Email corporativo</label>
                    <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="tu@empresa.com" />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 13, color: C.textMuted, display: 'block', marginBottom: 6, fontFamily: 'Figtree' }}>Empresa</label>
                  <input required value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} placeholder="Nombre de tu empresa" />
                </div>
                <div>
                  <label style={{ fontSize: 13, color: C.textMuted, display: 'block', marginBottom: 6, fontFamily: 'Figtree' }}>¿En qué podemos ayudarte?</label>
                  <textarea required rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} placeholder="¿Qué bases de datos usas? ¿Cuántos usuarios? ¿Necesidades específicas?" style={{ resize: 'vertical' }} />
                </div>
                <button type="submit" className="btn-primary" style={{ justifyContent: 'center', fontSize: 16, padding: '15px' }}>
                  Enviar mensaje →
                </button>
                <p style={{ fontSize: 12, color: C.textFaint, textAlign: 'center', fontFamily: 'Figtree' }}>
                  O escríbenos directo: <a href="mailto:chateacontuempresa@gmail.com" style={{ color: C.cyan }}>chateacontuempresa@gmail.com</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ setPage }) {
  const links = {
    'Producto': [
      { label: 'Características', id: 'caracteristicas', scroll: true },
      { label: 'Precios', id: 'precios', scroll: true },
      { label: 'Demo', id: 'demo', scroll: true },
      { label: 'Documentación', page: 'docs' },
      { label: 'API', page: 'api' },
    ],
    'Empresa': [
      { label: 'Sobre nosotros', page: 'about' },
      { label: 'Blog', page: 'blog' },
      { label: 'Casos de éxito', page: 'casos' },
      { label: 'Prensa', page: 'prensa' },
      { label: 'Contacto', id: 'contacto', scroll: true },
    ],
    'Soporte': [
      { label: 'FAQ', id: 'faq', scroll: true },
      { label: 'Centro de ayuda', page: 'ayuda' },
      { label: 'Términos de servicio', page: 'terminos' },
      { label: 'Política de privacidad', page: 'privacidad' },
      { label: 'SLA Enterprise', page: 'sla' },
    ],
  };

  return (
    <footer style={{ borderTop: `1px solid ${C.border}`, padding: 'clamp(40px,6vw,80px) clamp(16px,4vw,60px) 40px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 40, marginBottom: 48 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: C.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 16 }}>C</span>
              </div>
              <div>
                <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 15, color: C.text, letterSpacing: '-0.02em' }}>CORE</div>
                <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'Figtree' }}>chateacontuempresa.com</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.6, maxWidth: 220, marginBottom: 16, fontFamily: 'Figtree' }}>
              IA On-Premise para consulta de datos empresariales en lenguaje natural.
            </p>
            <a href="https://wa.me/50249899115" target="_blank" rel="noreferrer">
              <button style={{ background: '#25D36615', border: '1px solid #25D36640', borderRadius: 8, padding: '8px 14px', color: '#25D366', cursor: 'pointer', fontSize: 13, fontFamily: 'Bricolage Grotesque', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                📱 WhatsApp
              </button>
            </a>
          </div>

          {Object.entries(links).map(([section, items]) => (
            <div key={section}>
              <h4 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 13, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 16 }}>{section}</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map(({ label, id, scroll, page }) => (
                  <span key={label}
                    onClick={() => scroll ? document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }) : setPage(page)}
                    style={{ fontSize: 14, color: C.textMuted, cursor: 'pointer', transition: 'color 0.2s', fontFamily: 'Figtree' }}
                    onMouseEnter={e => e.currentTarget.style.color = C.text}
                    onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 28, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <p style={{ fontSize: 13, color: C.textFaint, fontFamily: 'Figtree' }}>© 2026 CORE — chateacontuempresa.com · Todos los derechos reservados</p>
          <div style={{ display: 'flex', gap: 20 }}>
            {['Privacidad','Términos','Cookies'].map(l => (
              <span key={l} style={{ fontSize: 13, color: C.textFaint, cursor: 'pointer', fontFamily: 'Figtree' }}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

function SubPage({ page, setPage }) {
  const content = {
    docs: { title: 'Documentación', icon: '📖', body: 'Guías de instalación para Windows Server, Linux y Docker. Conectores para MySQL, PostgreSQL, SQL Server y más. API REST documentada con Swagger.' },
    api: { title: 'Referencia API', icon: '⚡', body: 'API REST completa para integrar CORE con tus aplicaciones. Autenticación via JWT. Endpoints para consultas, dashboards y gestión de usuarios.' },
    about: { title: 'Sobre nosotros', icon: '🏢', body: 'Somos un equipo de ingenieros especializados en IA empresarial on-premise. Fundados en 2023, hemos ayudado a más de 100 empresas en Latinoamérica a democratizar el acceso a datos.' },
    blog: { title: 'Blog', icon: '✍️', body: 'Artículos sobre IA empresarial, bases de datos, seguridad on-premise y casos de uso reales de nuestros clientes.' },
    casos: { title: 'Casos de éxito', icon: '🏆', body: 'Descubre cómo empresas de retail, manufactura, banca y salud han transformado sus operaciones con CORE.' },
    prensa: { title: 'Prensa', icon: '📰', body: 'Para entrevistas y notas de prensa: chateacontuempresa@gmail.com' },
    ayuda: { title: 'Centro de ayuda', icon: '🆘', body: 'Artículos detallados, videos tutoriales y guías paso a paso para sacar el máximo provecho de CORE.' },
    terminos: { title: 'Términos de servicio', icon: '📋', body: 'Al adquirir una licencia de CORE, aceptas estos términos. El software se licencia, no se vende. Prohibida la redistribución. La licencia es válida para una instalación (servidor). Soporte técnico incluido según el plan.' },
    privacidad: { title: 'Política de privacidad', icon: '🔒', body: 'CORE opera 100% on-premise. No recopilamos, transmitimos ni almacenamos tus datos empresariales. Los datos de registro (nombre, email, empresa) se usan exclusivamente para la activación de licencia y soporte.' },
    sla: { title: 'SLA Enterprise', icon: '📊', body: 'Garantizamos 99.9% de uptime para el componente API de CORE. Tiempo de respuesta a incidentes críticos: 2 horas. Resolución de incidentes críticos: 24 horas. Gerente de cuenta asignado.' },
  };
  const c = content[page] || { title: page, icon: '📄', body: 'Contenido próximamente.' };

  return (
    <div style={{ minHeight: '100vh', padding: '100px clamp(16px,4vw,60px) 60px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <button onClick={() => setPage('home')} style={{ background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', color: C.textMuted, cursor: 'pointer', marginBottom: 32, fontFamily: 'Bricolage Grotesque', fontWeight: 600, fontSize: 13 }}>← Volver al inicio</button>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{c.icon}</div>
        <h1 style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 800, fontSize: 40, marginBottom: 24, color: C.text, letterSpacing: '-0.03em' }}>{c.title}</h1>
        <p style={{ fontSize: 17, color: C.textMuted, lineHeight: 1.8, fontFamily: 'Figtree' }}>{c.body}</p>
        <div style={{ marginTop: 40, background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28 }}>
          <p style={{ color: C.textMuted, fontSize: 14, fontFamily: 'Figtree' }}>¿Tienes preguntas? Contáctanos en <a href="mailto:chateacontuempresa@gmail.com" style={{ color: C.cyan }}>chateacontuempresa@gmail.com</a> o por <a href="https://wa.me/50249899115" target="_blank" rel="noreferrer" style={{ color: '#25D366' }}>WhatsApp</a></p>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [page, setPage] = useState('home');
  const [isDark, setIsDark] = useState(true);

  useEffect(() => {
    if (page === 'home') window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  useEffect(() => {
    const storedTheme = localStorage.getItem('color-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDarkTheme = storedTheme === 'dark' || (!storedTheme && prefersDark);

    setIsDark(isDarkTheme);
    if (isDarkTheme) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, []);

  const toggleTheme = () => {
    const html = document.documentElement;
    const willBeDark = !html.classList.contains('dark');

    if (willBeDark) {
      html.classList.add('dark');
      localStorage.setItem('color-theme', 'dark');
      setIsDark(true);
    } else {
      html.classList.remove('dark');
      localStorage.setItem('color-theme', 'light');
      setIsDark(false);
    }

    fetch('/login.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'action=toggle_theme&theme=' + (willBeDark ? 'dark' : 'light')
    }).catch(() => {});
  };

  return (
    <>
      <style>{css}</style>
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          #hamburger { display: flex !important; }
          [style*="gridTemplateColumns: span 2"] { grid-column: span 1 !important; }
        }
      `}</style>
      <NavHeader page={page} setPage={setPage} isDark={isDark} toggleTheme={toggleTheme} />
      {page === 'home' ? (
        <main>
          <HeroSection />
          <FeaturesSection />
          <DemoSection />
          <TestimonialsSection />
          <PricingSection />
          <FAQSection />
          <ContactSection />
        </main>
      ) : (
        <SubPage page={page} setPage={setPage} />
      )}
      <Footer setPage={setPage} />
    </>
  );
}